import React, { FC } from 'react';
import { Table } from 'shared/components';
import { Button } from '@vadiun/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faPencilAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { useVerifyAction } from '@vadiun/react-hooks-legacy';
import { RulesConfiguration } from '../models/RulesConfigurationModels';
import EyeToDetail from 'shared/components/Table/actions-buttons/EyeToDetail';

const TRAFFIC_LIGHT_RULES_CONFIGURATION_BASE =
  '/main/inventories/traffic-lights-management/traffic-light-rules-configuration';

const formatMovementDate = (date: string | null | undefined): string => {
  if (!date) return '-';

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleString('es-AR', { hour12: false });
};


interface Props {
  configurations: RulesConfiguration[];
  totalItems: number;
  rowsPerPage: number;
  onChangePage: (page: number, rowsPerPage: number) => void;
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  handlePageChange: (currentPage: number) => void;
  onDelete: (id: number) => Promise<void>;
  loadingDelete: boolean;
  reload: () => void;
}

const TrafficLightRulesConfigurationsTable: FC<Props> = ({
  configurations,
  totalItems,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  handlePageChange,
  onDelete,
  loadingDelete,
  reload
}) => {
  const verifyAction = useVerifyAction();

  const columns = [
    {
      name: 'name',
      label: 'Nombre',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({
          style: {
            color: '#0369A1'
          }
        })
      }
    },
    {
      name: 'last_movement',
      label: 'Último movimiento',
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({
          style: {
            textAlign: 'center'
          }
        }),
        customBodyRender: (lastMovement: RulesConfiguration['last_movement']) => {
          if (!lastMovement) {
            return <span className="text-sm text-gray-500">Sin movimientos</span>;
          }

          const movementDate = lastMovement.updated_at
         || lastMovement.created_at;
          const movementUser = lastMovement.user_name || '-';

          return (
            <div className="flex items-center gap-4">
              <span className="text-sm text-green-600">
                {formatMovementDate(movementDate)}
              </span>
              <FontAwesomeIcon
                  size="sm"
                  icon={faArrowRight}
                  className="text-green-700"
                />
              <span className="text-sm text-green-800">
               {movementUser}
              </span>
            </div>
          );
        },
      }
    },
    {
      name: 'id',
      label: 'Acciones',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({
          style: {
            textAlign: 'start',
            width: 'auto'
          }
        }),
        setCellProps: () => ({
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            width: 'auto'
          }
        }),
        customBodyRender: (id: number) => {
          const handleDeleteClick = () => {
            verifyAction({
              title: '¡Estás por eliminar una regla!',
              body: 'Esta acción eliminará la regla de forma permanente. ¿Estás seguro?',
              onAccept: async () => {
                await onDelete(id);
                reload();
              }
            });
          };

          return (
            <div className="flex gap-4 items-center">
              <EyeToDetail
                route={`${TRAFFIC_LIGHT_RULES_CONFIGURATION_BASE}/${id}?readOnly=true`}
              />
              <Link
                to={`${TRAFFIC_LIGHT_RULES_CONFIGURATION_BASE}/${id}`}
                className="inline-flex items-center"
              >
                <FontAwesomeIcon
                  size="lg"
                  color="dodgerblue"
                  icon={faPencilAlt}
                  className="hover:text-blue-600"
                  title="Editar regla"
                />
              </Link>
              <Button
                onClick={handleDeleteClick}
                variant="light"
                className="w-8 h-8 flex items-center justify-center"
                color="red"
                aria-label="Eliminar regla"
                disabled={loadingDelete}
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  size="sm"
                  className="hover:text-red-600"
                  title="Eliminar regla"
                />
              </Button>
            </div>
          );
        }
      }
    }
  ];

  const data = configurations.map((x) => ({ ...x }));

  return (
    <div className="w-full">
      <Table
        options={{
          filter: false,
          search: false,
          download: false,
          elevation: 0,
          selectableRows: 'none',
          print: false,
          serverSide: true,
          viewColumns: false,
          count: totalItems,
          rowsPerPage: rowsPerPage,
          onChangePage(page: number) {
            onChangePage(page + 1, rowsPerPage);
            handlePageChange(page + 1);
          },
          onChangeRowsPerPage(rowsPerPage: number) {
            onChangeRowsPerPage(rowsPerPage);
          }
        }}
        title=""
        data={data}
        columns={columns}
      />
    </div>
  );
};

export default TrafficLightRulesConfigurationsTable;

